<script lang="ts">
import { useRouter } from "vue-router";
import { useLoader } from "@/utils/loader";
import { useToast } from "@/utils/toast";
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  getCurrentInstance,
  watch
} from "vue";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import FavListNav from "@/components/organisms/mypage/FavListNav.vue";
import Header from "@/components/organisms/common/Header.vue";
import OrderCardSimple from "@/components/organisms/orders/OrderCardSimple.vue";
import { officeService } from "@/app/services/OfficeService";
import { orderService } from "@/app/services/OrderService";
import { orderStore } from "@/store/order";
import { SlimOrder } from "@/types/order/Order";
import { formatFromISOString, startOfToday } from "@/utils/date";
import { authStore } from "@/store/auth";
import { useErrorModal } from "@/utils/modal";
import {
  AnalyzeWatchLaterEvent,
  eventService
} from "@/app/services/EventService";
import { Evaluation } from "@/types/office/Evaluation";

const limit = 50;

export default defineComponent({
  components: {
    AppFooter,
    BlockBack,
    FavListNav,
    Header,
    OrderCardSimple
  },
  setup() {
    const { back, push } = useRouter();
    const loader = useLoader();
    const toast = useToast();
    const errorModal = useErrorModal();
    const instance = getCurrentInstance();

    // 日付で集約した案件リスト
    const orderListAggregateDate = ref<
      {
        date: string;
        orderList: SlimOrder[];
        isRegisteredWatchLater: boolean;
      }[]
    >([]);

    // 表示済みのページ数
    const shownPages = ref(1);

    // 案件リストを全て読み込んだか
    const isAllLoaded = ref(false);

    const officeIds = ref<number[]>([]);
    const evaluationList = ref<Evaluation[]>([]);

    //Login status
    const isLogin = computed(() => authStore.existToken);

    onMounted(async () => {
      try {
        loader.show();
        await fetchOrders();

        // イベントログを記録する。非同期で十分なのでawaitしない
        eventService.registerEvent(
          AnalyzeWatchLaterEvent.group,
          AnalyzeWatchLaterEvent.types.viewWatchLaterPage,
          undefined,
          JSON.stringify({
            watchLaterCount: orderStore.watchLaterOrderList.length
          })
        );
      } catch (e) {
        await errorModal.showErrorModal(e as Error);
      } finally {
        loader.hide();
      }

      // 画面下部までスクロールされたときに追加の案件を読み込む
      const observer = new IntersectionObserver(
        async ([entry]) => {
          // 画面下部までスクロールされる && 未読み込みの案件がある場合に追加の案件を読み込む
          if (entry.isIntersecting && !isAllLoaded.value) {
            await fetchAdditionalOrders();
          }
        },
        { threshold: 0 }
      );
      const bottomRef = instance?.proxy!.$refs.bottomRef as HTMLElement;
      observer.observe(bottomRef);
    });

    watch(
      () => officeIds.value,
      async newValue => {
        // 存在しない事業所のデータのみ追加する
        const existsOfficeIds = evaluationList.value.map(e => e.id);
        const requestOfficeIds = newValue.filter(
          officeId => !existsOfficeIds.includes(officeId)
        );
        if (requestOfficeIds.length === 0) {
          return;
        }
        // 現状足りない分の評価データを取得する
        try {
          const evaluations = await officeService.fetchEvaluations({
            officeIds: requestOfficeIds
          });
          evaluationList.value = [...evaluationList.value, ...evaluations];
        } catch (e) {
          await errorModal.showErrorModal(e as Error);
        }
      }
    );

    async function fetchOrders() {
      // 昨日以前の古い案件は削除する
      const now = startOfToday();
      orderStore.removeExpiredOrdersFromWatchLaterOrderList(now);

      const orders = await orderService.fetchOrdersByOrderIds({
        order__in: orderStore.watchLaterOrderList
          .filter(order => order.isHiring || order.isHiring === undefined)
          .map(order => order.id)
          .join(","),
        limit: limit,
        offset: (shownPages.value - 1) * limit
      });
      // 案件が全て読み込まれたらフラグを立てる
      if (orders.length < limit) {
        isAllLoaded.value = true;
      }
      officeIds.value = [...new Set(orders.map(o => o.office.id))];
      orders.forEach(order => {
        if (orderListAggregateDate.value.find(o => o.date === order.date)) {
          // 既に同じ日付の案件がある場合は同日日付のリストに要素を追加する
          orderListAggregateDate.value
            .find(o => o.date === order.date)!
            .orderList.push(order);
        } else {
          // 同じ日付の案件がない場合は日付ごと追加する
          orderListAggregateDate.value.push({
            date: order.date,
            orderList: [order],
            isRegisteredWatchLater: true
          });
        }
      });

      // 募集中の案件のみを抽出する
      const isHiringOrderIds = orders
        .filter(order => order.isHiring)
        .map(order => order.id);
      orderStore.filterHiringOrdersFromWatchLaterOrderList(isHiringOrderIds);
    }

    function formatDate(date: string) {
      return formatFromISOString(date, "M月d日(iiiii)");
    }

    function isRegisteredWatchLater(order: SlimOrder) {
      return !!orderStore.watchLaterOrderList.find(o => o.id === order.id);
    }

    async function onCardClick(order: SlimOrder) {
      await push({
        name: "OrdersDetail",
        params: { orderId: `${order.id}` },
        query: {
          transitionSource: "WatchLaterOrders"
        }
      });
    }

    function onWatchLaterChange(order: SlimOrder) {
      const isRegistered = isRegisteredWatchLater(order);
      if (isRegistered) {
        orderStore.removeFromWatchLaterOrderList(order.id);
      } else {
        orderStore.addToWatchLaterOrderList(order);
      }

      // stateの中から該当のorderを探してisRegisteredWatchLaterを更新する
      orderListAggregateDate.value = orderListAggregateDate.value.map(item => {
        if (item.date === order.date) {
          return {
            ...item,
            orderList: item.orderList.map(o => {
              if (o.id === order.id) {
                return {
                  ...o,
                  isRegisteredWatchLater: !isRegistered
                };
              } else {
                return o;
              }
            })
          };
        } else {
          return item;
        }
      });

      toast.toast(
        isRegistered
          ? "あとで見るリストから削除しました"
          : "あとで見るリストに登録しました",
        {
          toastClass: "toast-custom",
          toaster: "b-toaster-top-full",
          autoHideDelay: 2000
        }
      );
      // イベントログを記録する。非同期で十分なのでawaitしない
      eventService.registerEvent(
        AnalyzeWatchLaterEvent.group,
        isRegistered
          ? AnalyzeWatchLaterEvent.types.removeOnWatchLaterPage
          : AnalyzeWatchLaterEvent.types.addOnWatchLaterPage,
        undefined,
        JSON.stringify({
          watchLaterCount: orderStore.watchLaterOrderList.length,
          orderId: order.id
        })
      );
    }

    function onClickBack() {
      back();
    }

    async function fetchAdditionalOrders() {
      try {
        loader.show();
        shownPages.value += 1;
        await fetchOrders();
      } catch (e) {
        await errorModal.showErrorModal(e as Error);
      } finally {
        loader.hide();
      }
    }

    function getEvaluation(order: SlimOrder) {
      return evaluationList.value.find(e => e.id === order.office.id) || null;
    }

    return {
      orderListAggregateDate,
      isLogin,
      formatDate,
      isRegisteredWatchLater,
      onCardClick,
      onWatchLaterChange,
      onClickBack,
      getEvaluation
    };
  }
});
</script>

<template>
  <div>
    <Header>
      お気に入り
    </Header>
    <FavListNav />
    <div class="pt-2">
      <div v-for="{ date, orderList } in orderListAggregateDate" :key="date">
        <div class="mx-2 my-2" data-testid="formatted-order-date">
          {{ formatDate(date) }}
        </div>
        <OrderCardSimple
          v-for="order in orderList"
          :key="order.id"
          :order="order"
          :isLogin="isLogin"
          :isRegisteredWatchLater="isRegisteredWatchLater(order)"
          :isShowAdditionalLabel="true"
          :evaluation="getEvaluation(order)"
          @card-click="onCardClick(order)"
          @change-watch-later="onWatchLaterChange(order)"
          class="my-2 mx-2"
        />
      </div>
      <p
        v-if="orderListAggregateDate.length === 0"
        class="p-3 m-3 bg-white text-size-14 text-center"
      >
        あとで見るに追加されたお仕事はありません
      </p>
      <div ref="bottomRef" />
    </div>
    <AppFooter />
    <BlockBack />
  </div>
</template>

<style lang="scss" scoped></style>
